import type {
	BlogInteractionsData,
	Comment,
	CreateCommentInput,
	NodeBlock,
	Note,
} from "@adafia/types";
import { BlockInteractions, MarkdownRenderer } from "@adafia/ui";
import { useState } from "react";
import { MarginCommentThread } from "./MarginCommentThread";

interface PostContentProps {
	post: Note;
	blocks?: NodeBlock[];
	interactions?: BlogInteractionsData;
	comments?: Comment[];
	onBlockLike: (blockId: string) => void;
	onCommentSubmit: (data: Omit<CreateCommentInput, "nodeId">) => void;
	onCommentDelete?: (commentId: string) => void;
	isCommentPending: boolean;
}

export const PostContent = ({
	post,
	blocks,
	interactions,
	comments,
	onBlockLike,
	onCommentSubmit,
	onCommentDelete,
	isCommentPending,
}: PostContentProps) => {
	const [activeCommentBlockId, setActiveCommentBlockId] = useState<
		string | null
	>(null);

	if (!blocks || blocks.length === 0) {
		return (
			<div className="blog-content">
				<MarkdownRenderer content={post.content || ""} />
			</div>
		);
	}

	return (
		<div className="blog-content">
			{blocks.map((block) => {
				const blockMeta = interactions?.blocks.find(
					(b: { blockId: string }) => b.blockId === block.id,
				);
				const blockCommentsCount =
					comments?.filter((c) => c.blockId === block.id).length || 0;
				const blockComments =
					comments?.filter((c) => c.blockId === block.id) || [];
				const isCommenting = activeCommentBlockId === block.id;

				return (
					<div key={block.id} className="block-wrapper">
						<div className="block-container" style={{ position: "relative" }}>
							<MarkdownRenderer content={block.content || ""} />
							{block.id && (
								<BlockInteractions
									blockId={block.id}
									likes={blockMeta?.likes || 0}
									userHasLiked={blockMeta?.userHasLiked}
									commentCount={blockCommentsCount}
									isCommenting={isCommenting}
									onLike={onBlockLike}
									onComment={(bid) =>
										setActiveCommentBlockId(isCommenting ? null : bid)
									}
								/>
							)}
						</div>

						{block.id && (
							<MarginCommentThread
								blockId={block.id}
								comments={blockComments}
								isActive={isCommenting}
								isLoading={isCommentPending}
								onOpen={() => setActiveCommentBlockId(block.id as string)}
								onClose={() => setActiveCommentBlockId(null)}
								onSubmit={onCommentSubmit}
								onCommentDelete={onCommentDelete}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};
